import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ConfirmDialog } from './ConfirmDialog';

export type PreviewPersona = {
  name: string;
  jobTitle: string;
  type: string;
  agenda: string;
  typicalObjections: string;
  /** male | female | nonbinary — optional */
  gender?: string | null;
  /** Optional video avatar id for verbal practice sessions */
  videoAvatarId?: string | null;
};

export type PersonaCompleteness = {
  sections: {
    company: boolean;
    product: boolean;
    sales: boolean;
    competitors: boolean;
  };
  filledCount: number;
  totalCount: number;
};

const PERSONA_TYPES = [
  { value: 'champion', label: 'Champion' },
  { value: 'blocker', label: 'Blocker' },
  { value: 'skeptic', label: 'Skeptic' },
  { value: 'economic_buyer', label: 'Economic buyer' },
  { value: 'technical_evaluator', label: 'Technical evaluator' },
  { value: 'end_user', label: 'End user' },
];

const SECTION_LABELS: { key: keyof PersonaCompleteness['sections']; label: string }[] = [
  { key: 'company', label: 'Company' },
  { key: 'product', label: 'Product' },
  { key: 'sales', label: 'Sales' },
  { key: 'competitors', label: 'Competitors' },
];

type Props = {
  open: boolean;
  personas: PreviewPersona[];
  completeness: PersonaCompleteness | null;
  onClose: () => void;
  onConfirmSave: (personas: PreviewPersona[]) => Promise<void>;
  onRegenerate: () => Promise<{ personas: PreviewPersona[]; completeness: PersonaCompleteness }>;
  isSaving?: boolean;
};

export function PersonaGeneratePreview({
  open,
  personas: initialPersonas,
  completeness,
  onClose,
  onConfirmSave,
  onRegenerate,
  isSaving = false,
}: Props) {
  const [rows, setRows] = useState<PreviewPersona[]>(initialPersonas);
  const [regenerating, setRegenerating] = useState(false);
  const [regenerateConfirmOpen, setRegenerateConfirmOpen] = useState(false);

  useEffect(() => {
    if (open) {
      setRows(initialPersonas.map((p) => ({ ...p })));
    }
  }, [open, initialPersonas]);

  if (!open) return null;

  const updateRow = (index: number, patch: Partial<PreviewPersona>) => {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  };

  const runRegenerate = async () => {
    setRegenerating(true);
    try {
      const next = await onRegenerate();
      setRows(next.personas.map((p) => ({ ...p })));
    } finally {
      setRegenerating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto my-8">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-start gap-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Review generated personas</h2>
            <p className="text-sm text-gray-600 mt-1">
              Edit any field, then save. You can regenerate from the latest business profile data
              anytime.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-sm shrink-0"
          >
            Close
          </button>
        </div>

        <div className="px-6 py-4 border-b bg-gray-50">
          <p className="text-sm font-medium text-gray-800 mb-2">Business profile data used</p>
          {completeness ? (
            <>
              <div className="flex flex-wrap gap-2 mb-2">
                {SECTION_LABELS.map(({ key, label }) => (
                  <Link
                    key={key}
                    to={`/onboarding?section=${key}`}
                    className={`text-xs px-2 py-1 rounded inline-block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-600 focus-visible:ring-offset-1 ${
                      completeness.sections[key]
                        ? 'bg-green-100 text-green-800 hover:underline'
                        : 'bg-gray-200 text-gray-600 hover:underline'
                    }`}
                  >
                    {completeness.sections[key] ? '✓ ' : '○ '}
                    {label}
                  </Link>
                ))}
              </div>
              <p className="text-xs text-gray-600">
                {completeness.filledCount < completeness.totalCount
                  ? 'Fill out more sections in your business profile to improve persona accuracy.'
                  : 'All main sections have data — personas should be well grounded.'}
              </p>
              <Link
                to="/onboarding"
                className="text-xs text-red-700 hover:underline mt-1 inline-block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-600 focus-visible:ring-offset-1 rounded"
              >
                Open business profile
              </Link>
            </>
          ) : (
            <p className="text-xs text-gray-600">Completeness data not available.</p>
          )}
        </div>

        <div className="p-6 space-y-6">
          {rows.map((row, index) => (
            <div key={index} className="border rounded-lg p-4 space-y-3 bg-white">
              <p className="text-sm font-medium text-gray-500">Persona {index + 1}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Name</label>
                  <input
                    type="text"
                    value={row.name}
                    onChange={(e) => updateRow(index, { name: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Job title</label>
                  <input
                    type="text"
                    value={row.jobTitle}
                    onChange={(e) => updateRow(index, { jobTitle: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
                <select
                  value={row.type}
                  onChange={(e) => updateRow(index, { type: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md text-sm"
                >
                  {PERSONA_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Gender (optional)
                  </label>
                  <select
                    value={row.gender ?? ''}
                    onChange={(e) =>
                      updateRow(index, {
                        gender: e.target.value === '' ? null : e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border rounded-md text-sm"
                  >
                    <option value="">Not specified</option>
                    <option value="female">Female</option>
                    <option value="male">Male</option>
                    <option value="nonbinary">Non-binary</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Video avatar ID (optional)
                  </label>
                  <input
                    type="text"
                    value={row.videoAvatarId ?? ''}
                    onChange={(e) => updateRow(index, { videoAvatarId: e.target.value || null })}
                    placeholder="Assign a video avatar"
                    className="w-full px-3 py-2 border rounded-md text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Agenda</label>
                <textarea
                  value={row.agenda}
                  onChange={(e) => updateRow(index, { agenda: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border rounded-md text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Typical objections
                </label>
                <textarea
                  value={row.typicalObjections}
                  onChange={(e) => updateRow(index, { typicalObjections: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border rounded-md text-sm"
                />
              </div>
            </div>
          ))}
        </div>

        <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex flex-wrap gap-3 justify-end">
          <button
            type="button"
            onClick={onClose}
            className="border text-gray-700 px-4 py-2 rounded-md text-sm"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => setRegenerateConfirmOpen(true)}
            disabled={regenerating || isSaving}
            className="border border-red-300 text-red-800 px-4 py-2 rounded-md text-sm hover:bg-red-50 disabled:opacity-50"
          >
            {regenerating ? 'Regenerating…' : 'Regenerate'}
          </button>
          <button
            type="button"
            onClick={() => onConfirmSave(rows)}
            disabled={isSaving || regenerating}
            className="bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-red-800 disabled:opacity-50"
          >
            {isSaving ? 'Saving…' : 'Confirm and save'}
          </button>
        </div>
      </div>
      <ConfirmDialog
        open={regenerateConfirmOpen}
        title="Regenerate personas?"
        description="Your current edits in this preview will be replaced."
        confirmLabel="Regenerate"
        onConfirm={() => {
          setRegenerateConfirmOpen(false);
          void runRegenerate();
        }}
        onCancel={() => setRegenerateConfirmOpen(false)}
        loading={regenerating}
      />
    </div>
  );
}
